import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";
import { vehicleTypeLabel } from "../../utils/vehicleTypeLabel";

type FleetTab = "standard" | "executive";

type VehicleTypeOption = {
  id: string;
  name?: string | null;
  description: string;
  seatCapacity: number | null;
  isMileageBase: boolean;
};

type LocationPriceRow = {
  id: string;
  referenceNumber: number;
  vehicleTypeId: string;
  fleetCategory: string;
  startPostCode: string;
  startRadiusMiles: number;
  finishPostCode: string;
  finishRadiusMiles: number;
  price: number;
  status: boolean;
  vehicleType?: VehicleTypeOption;
};

const emptyForm = {
  vehicleTypeId: "",
  startPostCode: "",
  startRadiusMiles: "13",
  finishPostCode: "",
  finishRadiusMiles: "13",
  price: "",
  createReverse: true,
  autoUpliftOtherVehicles: true,
};

function vehicleLabel(v: VehicleTypeOption | undefined): string {
  if (!v) return "—";
  return vehicleTypeLabel(v);
}

export default function LocationPrices() {
  const [fleetTab, setFleetTab] = useState<FleetTab>("standard");
  const [rows, setRows] = useState<LocationPriceRow[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pricesRes, typesRes] = await Promise.all([
        axiosInstance.get("/location-prices", { params: { fleetCategory: fleetTab } }),
        axiosInstance.get("/location-prices/vehicle-types", {
          params: { fleetCategory: fleetTab },
        }),
      ]);
      setRows(pricesRes.data);
      setVehicleTypes(typesRes.data);
      const base =
        typesRes.data.find((v: VehicleTypeOption) => v.isMileageBase) ??
        typesRes.data.find((v: VehicleTypeOption) => (v.seatCapacity ?? 99) <= 4);
      setForm((f) => ({
        ...f,
        vehicleTypeId: f.vehicleTypeId || base?.id || typesRes.data[0]?.id || "",
      }));
    } catch {
      setMessage("Failed to load location prices");
    } finally {
      setLoading(false);
    }
  }, [fleetTab]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    const base =
      vehicleTypes.find((v) => v.isMileageBase) ??
      vehicleTypes.find((v) => (v.seatCapacity ?? 99) <= 4);
    setForm({
      ...emptyForm,
      vehicleTypeId: base?.id ?? vehicleTypes[0]?.id ?? "",
    });
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        vehicleTypeId: form.vehicleTypeId,
        fleetCategory: fleetTab,
        startPostCode: form.startPostCode,
        startRadiusMiles: Number(form.startRadiusMiles),
        finishPostCode: form.finishPostCode,
        finishRadiusMiles: Number(form.finishRadiusMiles),
        price: Number(form.price),
        status: true,
        createReverse: form.createReverse,
        autoUpliftOtherVehicles: form.autoUpliftOtherVehicles,
      };

      if (editingId) {
        await axiosInstance.put(`/location-prices/${editingId}`, payload);
        setMessage("Location price updated");
      } else {
        await axiosInstance.post("/location-prices", payload);
        setMessage("Location price(s) added");
      }
      resetForm();
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Save failed";
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: LocationPriceRow) => {
    setEditingId(row.id);
    setForm({
      vehicleTypeId: row.vehicleTypeId,
      startPostCode: row.startPostCode,
      startRadiusMiles: String(row.startRadiusMiles),
      finishPostCode: row.finishPostCode,
      finishRadiusMiles: String(row.finishRadiusMiles),
      price: String(row.price),
      createReverse: false,
      autoUpliftOtherVehicles: false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this location price?")) return;
    await axiosInstance.delete(`/location-prices/${id}`);
    await load();
  };

  const baseVehicle = vehicleTypes.find((v) => v.id === form.vehicleTypeId);

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageBreadcrumb pageTitle="Location Prices (LPs)" />
        <div className="flex gap-3 text-sm">
          <Link to="/vehicle-type" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
            Add Fleet Type
          </Link>
          <span className="text-gray-300">|</span>
          <a
            href="https://www.gov.uk/find-postcode"
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
          >
            Help
          </a>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 max-w-3xl">
        Set fixed prices between two zones in the UK. Each zone is a postcode plus a radius in
        miles. Location prices take priority over per-mile and minimum fare for the same route.
        Entering a price for the base 4-passenger saloon can automatically create prices for other
        vehicle sizes using your uplift settings from Per Mile Prices.
      </p>

      <div className="mt-4 flex gap-2 border-b">
        {(["standard", "executive"] as FleetTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setFleetTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              fleetTab === tab
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h2 className="text-lg font-semibold text-gray-800">
          {editingId ? "Edit location price" : "Add a location price for a 4-seater"}
        </h2>
        {!editingId && (
          <p className="text-sm text-gray-500 mt-1">
            Choose the base vehicle type; other fleet sizes can be filled automatically.
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle type
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={form.vehicleTypeId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, vehicleTypeId: e.target.value }))
                }
                required
              >
                {vehicleTypes.map((v) => (
                  <option key={v.id} value={v.id}>
                    {vehicleLabel(v)}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start (postcode)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 border rounded-lg px-3 py-2 uppercase"
                    placeholder="e.g. SW1A 1AA"
                    value={form.startPostCode}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, startPostCode: e.target.value }))
                    }
                    required
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">within</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    className="w-16 border rounded-lg px-2 py-2"
                    value={form.startRadiusMiles}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, startRadiusMiles: e.target.value }))
                    }
                    required
                  />
                  <span className="text-sm text-gray-500">miles</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Finish (postcode)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 border rounded-lg px-3 py-2 uppercase"
                    placeholder="e.g. SL6 7RN"
                    value={form.finishPostCode}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, finishPostCode: e.target.value }))
                    }
                    required
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">within</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    className="w-16 border rounded-lg px-2 py-2"
                    value={form.finishRadiusMiles}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, finishRadiusMiles: e.target.value }))
                    }
                    required
                  />
                  <span className="text-sm text-gray-500">miles</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Single price (£)
              </label>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">£</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="flex-1 border rounded-lg px-3 py-2"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  required
                />
              </div>
            </div>
          </div>

          {!editingId && (
            <div className="flex flex-col gap-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.createReverse}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, createReverse: e.target.checked }))
                  }
                />
                Also create a price for the reverse direction
              </label>
              {baseVehicle &&
                (baseVehicle.isMileageBase || (baseVehicle.seatCapacity ?? 99) <= 4) && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.autoUpliftOtherVehicles}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          autoUpliftOtherVehicles: e.target.checked,
                        }))
                      }
                    />
                    Automatically create prices for other vehicle sizes (using uplifts)
                  </label>
                )}
            </div>
          )}

          <div className="min-h-[80px] rounded-lg border border-dashed border-gray-200 bg-gray-50/80 flex items-center justify-center text-sm text-gray-400">
            Zone preview — match by postcode prefix; radius applies when booking coordinates are
            available
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-brand-500 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? "Saving..." : editingId ? "Update" : "+ Add"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border rounded-lg text-gray-700"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
        {message && (
          <p
            className={`mt-3 text-sm ${
              message.includes("Failed") || message.includes("failed")
                ? "text-red-600"
                : "text-green-600"
            }`}
          >
            {message}
          </p>
        )}
      </section>

      <section className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 className="text-lg font-semibold px-6 pt-6 pb-2">Locations</h2>
        {loading ? (
          <p className="px-6 py-8 text-gray-500">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left whitespace-nowrap">ID</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Vehicle type</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Pickup</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Pickup miles</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Drop off</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Drop off miles</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Price</th>
                  <th className="px-4 py-2 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No location prices for {fleetTab} fleet
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-600">
                        {row.referenceNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {vehicleLabel(row.vehicleType)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs uppercase whitespace-nowrap">
                        {row.startPostCode}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.startRadiusMiles}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs uppercase whitespace-nowrap">
                        {row.finishPostCode}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.finishRadiusMiles}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        £{Number(row.price).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                          onClick={() => startEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline"
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
